import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  LocationService,
  Country,
  State,
  City,
} from "@/lib/api/services/locationService";

interface LocationState {
  countries: Country[];
  states: State[];
  cities: City[];
  selectedCountry: string | null;
  selectedState: string | null;
  countriesLoading: boolean;
  statesLoading: boolean;
  citiesLoading: boolean;
  error: string | null;
}

const initialState: LocationState = {
  countries: [],
  states: [],
  cities: [],
  selectedCountry: null,
  selectedState: null,
  countriesLoading: false,
  statesLoading: false,
  citiesLoading: false,
  error: null,
};

const handleError = (error: unknown): string => {
  return error instanceof Error
    ? error.message
    : "An unexpected error occurred";
};

export const fetchCountries = createAsyncThunk(
  "location/fetchCountries",
  async (_, { rejectWithValue }) => {
    try {
      return await LocationService.getCountries();
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

export const fetchStates = createAsyncThunk(
  "location/fetchStates",
  async (countryCode: string, { rejectWithValue }) => {
    try {
      return await LocationService.getStates(countryCode);
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

export const fetchCities = createAsyncThunk(
  "location/fetchCities",
  async (
    { countryCode, stateCode }: { countryCode: string; stateCode: string },
    { rejectWithValue }
  ) => {
    try {
      return await LocationService.getCities(countryCode, stateCode);
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

const locationSlice = createSlice({
  name: "location",
  initialState,
  reducers: {
    setSelectedCountry: (state, action: PayloadAction<string>) => {
      state.selectedCountry = action.payload;
      state.states = [];
      state.cities = [];
      state.selectedState = null;
    },
    setSelectedState: (state, action: PayloadAction<string>) => {
      state.selectedState = action.payload;
      state.cities = [];
    },
    resetLocation: (state) => {
      state.selectedCountry = null;
      state.selectedState = null;
      state.states = [];
      state.cities = [];
      state.error = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCountries.pending, (state) => {
        state.countriesLoading = true;
        state.error = null;
      })
      .addCase(fetchCountries.fulfilled, (state, action) => {
        state.countriesLoading = false;
        state.countries = action.payload;
      })
      .addCase(fetchCountries.rejected, (state, action) => {
        state.countriesLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(fetchStates.pending, (state) => {
        state.statesLoading = true;
        state.error = null;
      })
      .addCase(fetchStates.fulfilled, (state, action) => {
        state.statesLoading = false;
        state.states = action.payload;
      })
      .addCase(fetchStates.rejected, (state, action) => {
        state.statesLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(fetchCities.pending, (state) => {
        state.citiesLoading = true;
        state.error = null;
      })
      .addCase(fetchCities.fulfilled, (state, action) => {
        state.citiesLoading = false;
        state.cities = action.payload;
      })
      .addCase(fetchCities.rejected, (state, action) => {
        state.citiesLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setSelectedCountry,
  setSelectedState,
  resetLocation,
  clearError,
} = locationSlice.actions;

export default locationSlice.reducer;
